import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  AlertCircleIcon,
  CircleCheckIcon,
  LoaderIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import type { ActivityEntry } from './types';

/**
 * Inline operational chip rendered under the latest assistant
 * bubble. Three states: running (pulsing spinner), ok (green
 * check), failed (red alert). The label is the tool name with a
 * sentence-cased "Doing X" prefix translated via `m-agent.tools.*`.
 */
export const ActivityChip = ({ activity }: { activity: ActivityEntry }) => {
  const { t } = useTranslation('m-agent');
  const label = activity.label
    ? activity.name === 'create_order'
      ? t('confirmation.batch.creating_for', {
          client: activity.label,
          defaultValue: 'Creating order for {{client}}',
        })
      : activity.label
    : t(`tools.${activity.name}`, sentenceCase(activity.name));

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 340, damping: 24 }}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium tracking-tight',
        activity.status === 'running' && 'bg-primary-50 text-primary-700',
        activity.status === 'ok' && 'bg-slate-100 text-slate-600',
        activity.status === 'failed' && 'bg-red-50 text-red-700',
      )}
      title={activity.errorMessage}
    >
      {activity.status === 'running' && (
        <LoaderIcon className='size-3 animate-spin' />
      )}
      {activity.status === 'ok' && <CircleCheckIcon className='size-3 text-emerald-500' />}
      {activity.status === 'failed' && (
        <AlertCircleIcon className='size-3' />
      )}
      <span>{label}</span>
    </motion.div>
  );
};

const sentenceCase = (snake: string): string =>
  snake.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
